/** Planning the trip: pickup, any stops and the destination. */

import { ApiError, errorMessage, get } from "@taxi/mobile-web/api";
import { currentPosition, usePlaceSearch, useResource } from "@taxi/mobile-web/hooks";
import {
  BriefcaseIcon,
  ClockIcon,
  CloseIcon,
  HomeIcon,
  LocateIcon,
  PinIcon,
  PlusIcon,
  RepeatIcon,
  StarIcon,
} from "@taxi/mobile-web/icons";
import { navigate } from "@taxi/mobile-web/router";
import { IconButton, Spinner, TopBar } from "@taxi/mobile-web/ui";
import { useEffect, useRef, useState } from "react";
import brand from "../../../../brand/brand.json";
import { placeSubtitle, placeTitle, recentPlaces, rememberPlace, useDraft } from "../lib/draft";
import { useSession } from "../lib/session";
import type { LatLng, Place, SavedPlace } from "../lib/types";

type FieldKey = "pickup" | "dropoff" | number;

type Suggestion = {
  key: string;
  /** The heading over the first of a group. */
  group?: string;
  place: Place;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
};

const fromSaved = (p: SavedPlace): Place => ({
  address: p.address,
  postcode: p.postcode,
  lat: p.lat,
  lng: p.lng,
  name: p.label === "Home" || p.label === "Work" ? null : p.label,
});

export function WhereScreen() {
  const { firm } = useSession();
  const { draft, update } = useDraft();
  // "Where to?" first; the pickup is often where they are, which we can fill in.
  const [active, setActive] = useState<FieldKey>(
    !draft.dropoff ? "dropoff" : !draft.pickup ? "pickup" : "dropoff",
  );
  const [query, setQuery] = useState("");
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const places = useResource(() => get<{ data: SavedPlace[] }>("/customer/places"), []);
  const inputs = useRef(new Map<FieldKey, HTMLInputElement>());
  /** Set once the customer picks a pickup themselves, so finding their location doesn't overwrite it. */
  const pickedPickup = useRef(false);

  const near: LatLng = draft.pickup ?? brand.map.centre;
  const search = usePlaceSearch(query, near);
  const maxStops = firm.booking.maxStops;

  // Opened with a destination already (a saved place): start from where they are, if allowed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on opening.
  useEffect(() => {
    if (draft.pickup) return;
    navigator.permissions
      ?.query({ name: "geolocation" })
      .then((permission) => {
        if (permission.state === "granted") void locateMe(false);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    inputs.current.get(active)?.focus();
  }, [active]);

  function placeAt(key: FieldKey): Place | null {
    if (key === "pickup") return draft.pickup;
    if (key === "dropoff") return draft.dropoff;
    return draft.stops[key] ?? null;
  }

  function choose(key: FieldKey, place: Place) {
    if (key === "pickup") pickedPickup.current = true;
    rememberPlace(place);
    const next = {
      pickup: key === "pickup" ? place : draft.pickup,
      dropoff: key === "dropoff" ? place : draft.dropoff,
      stops:
        typeof key === "number" ? draft.stops.map((s, i) => (i === key ? place : s)) : draft.stops,
    };
    // A new journey needs new prices.
    update({ ...next, quotes: [], quoteId: null });
    setQuery("");
    const emptyStop = next.stops.findIndex((s) => !s.address);
    if (!next.pickup) setActive("pickup");
    else if (!next.dropoff) setActive("dropoff");
    else if (emptyStop >= 0) setActive(emptyStop);
    else navigate("/book/quotes");
  }

  async function locateMe(fromTap = true) {
    setLocating(true);
    setLocateError(null);
    try {
      const at = await currentPosition();
      const found = await get<{ data: Place | null }>(
        `/places/reverse?lat=${at.lat}&lng=${at.lng}`,
      );
      const place: Place = found.data
        ? { ...found.data, lat: at.lat, lng: at.lng }
        : {
            address: `${at.lat.toFixed(5)}, ${at.lng.toFixed(5)}`,
            postcode: null,
            lat: at.lat,
            lng: at.lng,
            name: null,
          };
      if (fromTap) choose("pickup", place);
      else if (!pickedPickup.current) {
        // Found in the background: fill in the pickup without moving them on.
        update({ pickup: place, quotes: [], quoteId: null });
        if (draft.dropoff) navigate("/book/quotes");
      }
    } catch (error) {
      if (fromTap) {
        setLocateError(
          error instanceof ApiError
            ? errorMessage(error)
            : (error as GeolocationPositionError).code === 1
              ? "Location is turned off for this site. Search for your pickup instead."
              : "We couldn't find where you are. Search for your pickup instead.",
        );
      }
    } finally {
      setLocating(false);
    }
  }

  function addStop() {
    if (draft.stops.length >= maxStops) return;
    update({
      stops: [...draft.stops, { address: "", postcode: null, lat: 0, lng: 0, name: null }],
    });
    setActive(draft.stops.length);
  }

  function removeStop(index: number) {
    update({ stops: draft.stops.filter((_, i) => i !== index), quotes: [], quoteId: null });
    setActive("dropoff");
  }

  function swap() {
    update({ pickup: draft.dropoff, dropoff: draft.pickup, quotes: [], quoteId: null });
  }

  const saved = places.data?.data ?? [];
  const suggestions: Suggestion[] = [];
  if (query.trim().length >= 2) {
    for (const place of search.results) {
      suggestions.push({
        key: `s-${place.lat},${place.lng},${place.address}`,
        place,
        icon: <PinIcon />,
        title: placeTitle(place),
        subtitle: placeSubtitle(place),
      });
    }
  } else {
    for (const [i, place] of saved.entries()) {
      suggestions.push({
        key: `p-${place.id}`,
        group: i === 0 ? "Saved" : undefined,
        place: fromSaved(place),
        icon:
          place.kind === "home" ? (
            <HomeIcon />
          ) : place.kind === "work" ? (
            <BriefcaseIcon />
          ) : (
            <StarIcon />
          ),
        title: place.label,
        subtitle: place.address,
      });
    }
    for (const [i, place] of recentPlaces().entries()) {
      suggestions.push({
        key: `r-${place.lat},${place.lng}`,
        group: i === 0 ? "Recent" : undefined,
        place,
        icon: <ClockIcon />,
        title: placeTitle(place),
        subtitle: placeSubtitle(place),
      });
    }
  }

  const fields: { key: FieldKey; label: string; placeholder: string }[] = [
    { key: "pickup", label: "Pickup", placeholder: "Pickup address or postcode" },
    ...draft.stops.map((_, i) => ({
      key: i as FieldKey,
      label: `Stop ${i + 1}`,
      placeholder: "Add a stop",
    })),
    { key: "dropoff", label: "Destination", placeholder: "Where to?" },
  ];

  return (
    <div className="where">
      <TopBar title="Plan your trip" back="/" />
      <div className="route-card">
        <div className="route-line" aria-hidden="true" />
        {fields.map((field) => {
          const value = placeAt(field.key);
          const isActive = active === field.key;
          return (
            <div key={String(field.key)} className={`route-field${isActive ? " active" : ""}`}>
              <span
                className={`route-dot route-dot-${typeof field.key === "number" ? "stop" : field.key}`}
              />
              <input
                ref={(element) => {
                  if (element) inputs.current.set(field.key, element);
                  else inputs.current.delete(field.key);
                }}
                aria-label={field.label}
                placeholder={field.placeholder}
                value={
                  isActive
                    ? query || (value?.address ? placeTitle(value) : "")
                    : value?.address
                      ? placeTitle(value)
                      : ""
                }
                onFocus={(e) => {
                  setActive(field.key);
                  setQuery("");
                  e.target.select();
                }}
                onChange={(e) => {
                  setActive(field.key);
                  setQuery(e.target.value);
                }}
                autoComplete="off"
                enterKeyHint="search"
              />
              {typeof field.key === "number" ? (
                <IconButton
                  label={`Remove stop ${field.key + 1}`}
                  onClick={() => removeStop(field.key as number)}
                >
                  <CloseIcon size={18} />
                </IconButton>
              ) : field.key === "pickup" && draft.dropoff && draft.pickup ? (
                <IconButton label="Swap pickup and destination" onClick={swap}>
                  <RepeatIcon size={18} />
                </IconButton>
              ) : null}
            </div>
          );
        })}
        {draft.stops.length < maxStops && (
          <button type="button" className="add-stop" onClick={addStop}>
            <PlusIcon size={16} /> Add a stop
          </button>
        )}
      </div>

      <ul className="suggestions" aria-label="Suggestions">
        {active === "pickup" && query.trim().length < 2 && (
          <li>
            <button type="button" className="suggestion" onClick={() => void locateMe()}>
              <span className="suggestion-icon accent">
                {locating ? <Spinner small /> : <LocateIcon />}
              </span>
              <span className="suggestion-text">
                <strong>Use my current location</strong>
                {locateError ? (
                  <span className="error-text">{locateError}</span>
                ) : (
                  <span>We'll find the address</span>
                )}
              </span>
            </button>
          </li>
        )}
        {suggestions.map((s) => (
          <li key={s.key}>
            {s.group && <h2 className="suggestions-heading">{s.group}</h2>}
            <button type="button" className="suggestion" onClick={() => choose(active, s.place)}>
              <span className="suggestion-icon">{s.icon}</span>
              <span className="suggestion-text">
                <strong>{s.title}</strong>
                {s.subtitle && <span>{s.subtitle}</span>}
              </span>
            </button>
          </li>
        ))}
        {query.trim().length >= 2 && search.loading && suggestions.length === 0 && (
          <li className="suggestions-status">
            <Spinner small /> Searching…
          </li>
        )}
        {query.trim().length >= 2 && !search.loading && suggestions.length === 0 && (
          <li className="suggestions-status">
            {search.error ?? "No matches. Try a postcode, or the street and town."}
          </li>
        )}
      </ul>
    </div>
  );
}
